import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CareCategories, ProviderTypes } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { useLagoon } from '../useLagoon';

export const useSmartChoice = (ffAndSupressionFFCheckOnly = false): boolean => {
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enablePSXProviderRecommendations = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PSX_PROVIDER_RECOMMENDATIONS
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue: chipValueFromStore } = chipStore;
  const queryString = window.location.search;
  const searchParams = new URLSearchParams(queryString);
  const chipValueFromParams = searchParams.get('chipValue');
  const sectionType = searchParams.get('sectionType');
  const chipValue = chipValueFromParams || chipValueFromStore;
  const primaryCareFromDashboard = CareCategories.PRIMARY_CARE.replace(' ', '');
  const isMedicalOrPrimaryCare = [
    CareCategories.MEDICAL_SPECIALISTS,
    CareCategories.PRIMARY_CARE,
    primaryCareFromDashboard,
  ].includes(chipValue as CareCategories);

  const isNameSearch = searchParams.get('searchType') === 'name';
  const isNotSpecialitySearch =
    searchParams.get('searchBySpeciality') === 'false';

  const { providerType = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      providerType: state.typeaheadSearchStore.providerType,
    }))
  );
  const isSmartChoiceSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.SMART_CHOICE
  );

  const { notGroupType, notFacilityType } = useMemo(
    () => ({
      notGroupType:
        sectionType?.toLowerCase() === ProviderTypes.GROUP?.toLowerCase(),
      notFacilityType:
        sectionType?.toLowerCase() === ProviderTypes.FACILITY?.toLowerCase(),
    }),
    [sectionType]
  );

  if (isNotSpecialitySearch || isNameSearch) {
    return false;
  }

  const baseCondition =
    !isSmartChoiceSuppressed && enablePSXProviderRecommendations;
  if (ffAndSupressionFFCheckOnly) {
    return baseCondition;
  } else {
    return (
      baseCondition &&
      isMedicalOrPrimaryCare &&
      ((providerType && providerType === ProviderTypes.PRACTITIONER) ||
        !providerType) &&
      !notGroupType &&
      !notFacilityType
    );
  }
};
